import type { Diagnostic } from "@codemirror/lint"
import type { JSONSchema7 } from "json-schema"
import type { EditorView } from "@codemirror/view"
import { hoverTooltip } from "@codemirror/view"
import { ScriptLanguage } from "./model"
import type { SupportedColorScheme } from "@/ui"

/**
 * Find the position of a JSON value in a document string given JSON pointer segments.
 */
function findValuePosition(
  text: string,
  segments: string[],
): { from: number; to: number } | null {
  let searchFrom = 0

  for (const segment of segments) {
    if (!segment) {
      continue
    }

    if (/^\d+$/.test(segment)) {
      const idx = parseInt(segment, 10)
      let depth = 0
      let itemCount = 0
      let inStr = false
      for (let j = searchFrom; j < text.length; j++) {
        const ch = text[j]
        if (ch === '"' && text[j - 1] !== "\\") {
          inStr = !inStr

          continue
        }
        if (inStr) {
          continue
        }
        if (ch === "[") {
          if (depth === 0) {
            depth++
            if (idx === 0) {
              searchFrom = j + 1

              break
            }

            continue
          }
          depth++
        } else if (ch === "]") {
          depth--
        } else if (ch === "," && depth === 1) {
          itemCount++
          if (itemCount === idx) {
            searchFrom = j + 1

            break
          }
        }
      }
    } else {
      const escaped = segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      const keyPattern = new RegExp(`"${escaped}"\\s*:`)
      const keyMatch = keyPattern.exec(text.slice(searchFrom))
      if (!keyMatch) {
        return null
      }
      searchFrom = searchFrom + keyMatch.index + keyMatch[0].length
    }
  }

  const remaining = text.slice(searchFrom)
  const valueMatch = remaining.match(
    /^\s*("(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|true|false|null|\{|\[)/,
  )
  if (!valueMatch?.[1]) {
    return null
  }

  const wsLen = remaining.match(/^\s*/)?.[0].length ?? 0
  const valueStart = searchFrom + wsLen
  const valueStr = valueMatch[1]

  if (valueStr === "{" || valueStr === "[") {
    const close = valueStr === "{" ? "}" : "]"
    let depth = 1
    let inStr = false
    for (let j = valueStart + 1; j < text.length; j++) {
      const ch = text[j]
      if (ch === '"' && text[j - 1] !== "\\") {
        inStr = !inStr

        continue
      }
      if (inStr) {
        continue
      }
      if (ch === valueStr) {
        depth++
      } else if (ch === close) {
        depth--
        if (depth === 0) {
          return { from: valueStart, to: j + 1 }
        }
      }
    }

    return { from: valueStart, to: valueStart + 1 }
  }

  return { from: valueStart, to: valueStart + valueStr.length }
}

const languageExtension = async (
  language: ScriptLanguage | "json",
  jsonSchema?: JSONSchema7,
) => {
  switch (language) {
    case ScriptLanguage.JavaScript:
      return [(await import("@codemirror/lang-javascript")).javascript()]
    case ScriptLanguage.Python:
      return [(await import("@codemirror/lang-python")).python()]
    case "json": {
      if (jsonSchema) {
        const {
          jsonSchemaHover,
          jsonCompletion,
          stateExtensions,
          handleRefresh,
        } = await import("codemirror-json-schema")
        const { Draft07 } = await import("json-schema-library")
        const { json, jsonLanguage } = await import("@codemirror/lang-json")
        const { linter, lintGutter } = await import("@codemirror/lint")

        // Use Draft07 directly instead of jsonSchemaLinter() which uses Draft04.
        // Draft04 does not support if/then/else and silently skips nested validation.
        const draft = new Draft07(jsonSchema)

        const draft07Linter = (view: EditorView): Diagnostic[] => {
          const text = view.state.doc.toString()
          if (!text.length) {
            return []
          }

          let data: unknown
          try {
            data = JSON.parse(text)
          } catch {
            return []
          }

          const errors = draft.validate(data) as {
            data?: { pointer?: string }
            message?: string
          }[]
          if (!errors.length) {
            return []
          }

          return errors.reduce((acc: Diagnostic[], error) => {
            const pointer = error.data?.pointer
            if (!pointer) {
              return acc
            }

            const errorMessage = error.message ?? "Validation error"
            const segments = pointer
              .replace(/^#\/?/, "")
              .split("/")
              .filter(Boolean)

            if (segments.length === 0) {
              acc.push({
                from: 0,
                to: 0,
                message: errorMessage,
                severity: "error",
              })

              return acc
            }

            const pos = findValuePosition(text, segments)
            if (pos) {
              acc.push({
                from: pos.from,
                to: pos.to,
                message: errorMessage,
                severity: "error",
              })
            }

            return acc
          }, [])
        }

        return [
          json(),
          lintGutter(),
          linter(draft07Linter, { needsRefresh: handleRefresh }),
          jsonLanguage.data.of({ autocomplete: jsonCompletion() }),
          hoverTooltip(jsonSchemaHover()),
          stateExtensions(jsonSchema),
        ]
      }

      return [(await import("@codemirror/lang-json")).json()]
    }
  }
}

export const getExtensions = async (
  language: ScriptLanguage | "json",
  jsonSchema?: JSONSchema7,
) => await languageExtension(language, jsonSchema)

export const getTheme = async (colorScheme: SupportedColorScheme | undefined) =>
  (await import("@uiw/codemirror-theme-github"))[
    colorScheme === "dark" ? "githubDark" : "githubLight"
  ]
